import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * Display timezone for every timestamp in the app.
 *
 * The API speaks UTC exclusively — `models._iso` writes `...Z` and the decision and action
 * records use `isoformat()` on a UTC datetime. So nothing here converts data; this decides
 * only how an instant is *rendered*.
 *
 * Three states, not two. `auto` means "whatever this browser reports", and it is stored as the
 * literal string `auto` rather than as the resolved zone, so an analyst who travels, or who
 * picks up a laptop configured in another region, keeps following the device instead of being
 * pinned to wherever they first opened the page. An explicit choice is stored verbatim and
 * always wins — which matters for a shared incident bridge where everyone agrees to read
 * timestamps in the same zone regardless of where they are sitting.
 */
const STORAGE_KEY = 'ira.timezone';

/** The sentinel meaning "follow the browser". Not a valid IANA name, so it cannot collide. */
export const AUTO = 'auto';

export type TimezonePreference = typeof AUTO | string;

/** What this device reports, falling back to UTC if the runtime will not say. */
export function detectBrowserZone(): string {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (zone) return zone;
  } catch {
    // Intl is always present in supported browsers; this is belt and braces.
  }
  return 'UTC';
}

/**
 * Whether a zone name is one this runtime can actually format in.
 *
 * Checked rather than trusted because the stored value outlives the code that wrote it: a
 * zone can be renamed between tzdata releases, and a preference synced from another browser
 * may name something this one does not know. An unusable zone makes `toLocaleString` throw a
 * RangeError on every timestamp, which would take down every page that renders a date —
 * so an unknown name falls back to `auto` instead.
 */
export function isUsableZone(zone: string): boolean {
  if (zone === AUTO) return true;
  try {
    new Intl.DateTimeFormat('en-AU', { timeZone: zone }).format(new Date());
    return true;
  } catch {
    return false;
  }
}

function readStored(): TimezonePreference {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw && isUsableZone(raw)) return raw;
  } catch {
    // Private browsing denies localStorage.
  }
  return AUTO;
}

/**
 * Every zone this runtime knows, for the picker.
 *
 * `Intl.supportedValuesOf` is the only way to enumerate them and it is not universally
 * available, so a short fallback covers the zones this deployment actually cares about —
 * the Australian campuses, the offshore delivery sites, and UTC for reading raw records.
 */
const FALLBACK_ZONES = [
  'UTC',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Australia/Brisbane',
  'Australia/Adelaide',
  'Australia/Perth',
  'Australia/Darwin',
  'Australia/Hobart',
  'Pacific/Auckland',
  'Asia/Singapore',
  'Asia/Kolkata',
  'Asia/Manila',
  'Europe/London',
  'America/New_York',
  'America/Los_Angeles',
];

export function supportedZones(): string[] {
  try {
    const supported = (
      Intl as unknown as { supportedValuesOf?: (key: string) => string[] }
    ).supportedValuesOf?.('timeZone');
    if (supported?.length) return supported;
  } catch {
    // Fall through.
  }
  return FALLBACK_ZONES;
}

interface TimezoneContextValue {
  /** What the user chose: `auto`, or an explicit IANA name. */
  preference: TimezonePreference;
  /** The zone actually used for rendering. Never `auto`. */
  zone: string;
  /** True when `zone` came from the browser rather than from a deliberate choice. */
  isAuto: boolean;
  /** The zone this device reports, shown in the picker so `auto` is not opaque. */
  browserZone: string;
  setPreference: (next: TimezonePreference) => void;
}

const TimezoneContext = createContext<TimezoneContextValue | null>(null);

export function TimezoneProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<TimezonePreference>(readStored);
  const [browserZone, setBrowserZone] = useState<string>(detectBrowserZone);

  const setPreference = useCallback((next: TimezonePreference) => {
    const safe = isUsableZone(next) ? next : AUTO;
    setPreferenceState(safe);
    try {
      window.localStorage.setItem(STORAGE_KEY, safe);
    } catch {
      // Preference is lost on reload but the session still honours it.
    }
  }, []);

  // A laptop that changes timezone mid-session (travel, or a corrected system clock) should
  // follow while on `auto`. There is no event for this, so it is re-read on focus — cheap,
  // and it catches the realistic case of waking a machine in a new place.
  useEffect(() => {
    if (preference !== AUTO) return;
    const recheck = () => setBrowserZone(detectBrowserZone());
    window.addEventListener('focus', recheck);
    return () => window.removeEventListener('focus', recheck);
  }, [preference]);

  // Another tab changed the preference.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== STORAGE_KEY) return;
      setPreferenceState(readStored());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const value = useMemo<TimezoneContextValue>(() => {
    const isAuto = preference === AUTO;
    return {
      preference,
      zone: isAuto ? browserZone : preference,
      isAuto,
      browserZone,
      setPreference,
    };
  }, [preference, browserZone, setPreference]);

  return <TimezoneContext.Provider value={value}>{children}</TimezoneContext.Provider>;
}

export function useTimezone(): TimezoneContextValue {
  const context = useContext(TimezoneContext);
  if (!context) throw new Error('useTimezone must be used inside <TimezoneProvider>');
  return context;
}
